<template>
  <div>
    <div v-if="!isLoading">
      <div class="flex items-end">
        <div @click="navbarStore.openCalendarPopup()">
          <Calendar/>
        </div>

        <div v-if="navbarStore.calendarPopupOpen">
          <div class="bg-black bg-opacity-20 w-full h-full fixed left-0 right-0 top-0 bottom-0 z-[999]"></div>
          <div class="fixed right-6 rounded-lg bg-white w-[1450px] z-[9999999] top-[64px] h-custom-calendar shadow-soft2xl overflow-auto">
            <div class="">
              <div class="flex justify-end sticky top-0 bg-white p-4 shadow-softxs">
                <IconClose @click="navbarStore.closeCalendarPopup()"
                           class="fill-primary cursor-pointer"></IconClose>
              </div>

              <div class="space-y-5 max-w-7xl mx-auto flex flex-col justify-center">
                <div class="flex justify-between items-center whitespace-nowrap space-x-4">
                  <span class="text-md font-medium text-dark_primary">Legal Holidays in:</span>
                  <Multiselect
                      id="country"
                      name="country"
                      mode="tags"
                      :searchable="true"
                      :close-on-select="false"
                      v-model="selectedCountries"
                      placeholder="Choose countries"
                      :options="countries.map(el => { return {id:el.id, name:el.name, code:el.code, flagUrl:getFlag(el.code)} })"
                      :object="true"
                      value-prop="id"
                      label="name"
                      @select="validateLimit"
                  >
                    <template #placeholder>
                      <div class="w-full text-center text-gray-400">
                        Choose countries
                      </div>
                    </template>

                    <template v-slot:option="{ option }">
                    <span class="w-5 inline-block mr-2">
                      <img :src="option.flagUrl"/>
                    </span>
                      {{ option.name }}
                    </template>

                    <template #tag="{ option, handleTagRemove }">
                    <span class="multiselect-tag">
                      <span class="w-5 inline-block mr-2 align-middle">
                        <img :src="option.flagUrl" class="w-5 h-5 rounded-full"/>
                      </span>
                      <span class="align-middle">{{ option.name }}</span>
                      <span class="multiselect-tag-remove" @click="handleTagRemove(option, $event)">
                        &times;
                      </span>
                    </span>
                    </template>
                  </Multiselect>
                </div>

                <VueDatePicker
                    ref="datePickerRef"
                    id="datePickerId"
                    placeholder="Set a date for more details"
                    :enable-time-picker="false"
                    auto-apply
                    inline
                    class="w-full flex justify-center datepicker-scale"
                    :start-date="today.toDateString()"
                    :minDate="minDate.toDateString()"
                    :maxDate="maxDate.toDateString()"
                    :disabled-dates="countriesHolidays.map(el => {return el.holiday_date})"
                    :disabled-week-days="[6,0]"
                >
                  <template #day="day">
                    <div v-if="isWeekend(day.date)" class="relative flex justify-center items-center w-full h-full text-center group">
                      <AdvancedTooltip
                          theme="blue"
                          placement="top"
                          title="Weekend"
                      >
                      <span class="absolute top-0 left-1">
                        {{ day.day }}
                      </span>
                      </AdvancedTooltip>
                      <div class="flex flex-wrap justify-start items-center absolute bottom-0 left-1 gap-x-2 gap-y-1"
                           style="max-width: calc(6 * 1rem + 5 * 0.5rem);">
                        <template v-for="country in selectedCountries">
                          <span v-if="isHoliday(day.date, country)" class="w-4">
                            <AdvancedTooltip
                              theme="blue"
                              placement="top"
                              :title="getHolidayTooltip(day.date, country)">
                              <img :src="getFlag(country.code)" :alt="country.code"/>
                            </AdvancedTooltip>
                          </span>
                        </template>
                      </div>
                      <span class="absolute bottom-0 left-0 w-0 h-0.5 transition-all bg-gray-500 duration-300 group-hover:w-full"></span>
                    </div>

                    <div v-else class="relative flex justify-center items-center w-full h-full text-center group">
                      <span class="absolute top-0 left-1">
                        {{ day.day }}
                      </span>
                      <div class="flex flex-wrap justify-start items-center absolute bottom-0 left-1 gap-x-2 gap-y-1"
                          style="max-width: calc(6 * 1rem + 5 * 0.5rem);">
                        <template v-for="country in selectedCountries">
                          <span v-if="isHoliday(day.date, country)" class="w-4">
                            <AdvancedTooltip
                                theme="blue"
                                placement="top"
                                :title="getHolidayTooltip(day.date, country)">
                              <img :src="getFlag(country.code)" :alt="country.code"/>
                            </AdvancedTooltip>
                          </span>
                        </template>
                      </div>
                      <span class="absolute bottom-0 left-0 w-0 h-0.5 transition-all  duration-300 group-hover:w-full"
                            :class="{
                        'bg-red-500': isHoliday(day.date),
                        'bg-primary': !isHoliday(day.date)
                      }"></span>
                    </div>
                  </template>
                </VueDatePicker>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">

import IconClose from "@/components/svg_icons/IconClose.vue";
import {computed, ref} from "vue";
import {useApiStore} from "@/stores/api";
import {useNavbarStore} from "@/stores/navbar";
import IconCalendar from "@/components/svg_icons/IconCalendar.vue";
import {LegalHoliday, type LegalHolidayData} from "@/types/site_configuration/LegalHoliday";
import {Country, type CountryData} from "@/types/addresses/countries";
import Multiselect from "@vueform/multiselect";
import type {QueryFilter} from "@/types/object_classes/QueryFilter";
import {InterfaceFilterMultipleSeparator} from "@/types/object_classes/InterfaceListingFilter";
import VueDatePicker from "@vuepic/vue-datepicker";
import '@vuepic/vue-datepicker/dist/main.css'
import AdvancedTooltip from "@/components/tooltip/AdvancedTooltip.vue";
import {DateFilterHelper} from "@/helpers/DateFilterHelper";
import {showInfo} from "@/services/ShowMessages";
import Calendar from "@/components/widgets/navbar/Calendar.vue";

const api = useApiStore();
const navbarStore = useNavbarStore();
const legalHolidays = ref<LegalHoliday[]>([]);
const countries = ref<Country[]>([]);
const selectedCountries = ref<Country[]>([]);
const today = ref(new Date());
const isLoading = ref(false);

const currentYear = today.value.getFullYear();
const minDate = new Date(currentYear, 0, 1);       // 1 ian YYYY
const maxDate = new Date(currentYear + 1, 11, 31); // 31 dec YYYY+1
const maxCountries = 18;
const validateLimit = (option, _dup, ctx) => {
  if (selectedCountries.value.length > maxCountries) {
    ctx.remove(option);
    showInfo('You can select up to ' + maxCountries + ' countries.', '');
  }
}

const flags = import.meta.glob('@/assets/img/flags/1x1/*.svg', { eager: true, import: 'default' })

const getFlag = (code: string) => {
  if (code.toLowerCase() == 'xk') {
    return '';
  }

  let flag = flags[`/src/assets/img/flags/1x1/${code.toLowerCase()}.svg`];
  return flag ?? '';
}

const countriesHolidays = computed(() => {
  if (!selectedCountries.value.length) {
    return [];
  }

  let holidays: LegalHoliday[] = [];
  selectedCountries.value.forEach(country => {
    const countryHols = legalHolidays.value.filter(holiday => holiday.country_id === country.id);
    holidays = holidays.concat(countryHols);
  })

  return holidays;
});

const isHoliday = computed(() => (date: Date, country: Country|null = null) => {
  const dateStr = DateFilterHelper.FormatAsDateString(date);
  if (!country) {
    return countriesHolidays.value.some(h => h.holiday_date == dateStr);
  }
  return countriesHolidays.value.some(h => h.holiday_date == dateStr && h.country_id == country.id);
})

const isWeekend = computed(() => (date: Date) => {
  const day = date.getDay();
  return day === 0 || day === 6;
})

const getHolidayTooltip = (date: Date, country: Country) => {
  const dateStr = DateFilterHelper.FormatAsDateString(date);

  const holiday = countriesHolidays.value.find(h => h.holiday_date == dateStr && h.country_id == country.id);
  if (holiday) {
    return country.name + ' - ' + holiday.holiday_name;
  }

  return null;
};

const loadFromServer = () => {
  isLoading.value = true;

  const getLegalHolidays = api.getItems<LegalHoliday, LegalHolidayData>('legal-holidays', LegalHoliday);

  const countryFilter: QueryFilter = {
    delimiter: InterfaceFilterMultipleSeparator.SEPARATOR,
    field: "continent",
    values: ['Europe']
  }
  const getCountries = api.getItems<Country, CountryData>('countries', Country, undefined, undefined, [countryFilter]);
  Promise.allSettled([getLegalHolidays, getCountries])
      .then((result) => {
        if (result[0].status !== "fulfilled") {
          console.log("failed getting legal holidays data", result);
          return;
        }
        if (result[1].status !== "fulfilled") {
          console.log("failed getting countries data", result);
          return;
        }

        legalHolidays.value = result[0].value;
        countries.value = result[1].value;

        const ro = countries.value.find(c => c.code === 'RO');
        selectedCountries.value.push({id:ro.id, name:ro.name, code:ro.code, flagUrl:getFlag(ro.code)});
      })
      .finally(() => {
        isLoading.value = false;

      })
}
loadFromServer();

</script>
<style src="@vueform/multiselect/themes/default.css"></style>

<style scoped>
/* multiselect */
::v-deep(.slot-icon) {
  height: 20px;
  width: auto;
}

/* calendar */
/* Mărire font general */
::v-deep(.dp__main) {
  font-size: 1.1rem !important;
}

/* Mărire celule (zile) */
::v-deep(.dp__calendar .dp__cell_inner) {
  width: 12rem !important;
  height: 7rem !important;
  /*line-height: 8rem !important;*/
  font-size: 1.2rem !important;
}

/* Mărire header (luna și anul) */
::v-deep(.dp__month_year_row) {
  font-size: 1.5rem !important;
  padding: 0.65rem 0 !important;
}

/* Mărire nume zile săptămână */
::v-deep(.dp__calendar_header) {
  font-size: 1rem !important;
}

/* Spațiere mai mare între grile */
::v-deep(.dp__calendar) {
  padding: 1rem !important;
}

/* Border pentru ziua curentă */
::v-deep(.dp__today) {
  border-color: #5867dd !important;
}

/* Background pentru ziua selectata*/
::v-deep(.dp__active_date) {
  background-color: #5867dd !important;/* !* culoarea de fundal când e selectată *!*/
  color: white !important;             /* !* text color *!*/
  border-radius: 0.25rem !important;
}

/* Permite hover și pe datele disabled */
::v-deep(.dp__cell_disabled:hover) {
  background-color: var(--dp-hover-color) !important;
}
</style>